import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Button, Table, InputNumber } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { ColumnType } from 'antd/es/table'
import { HOURS_MAX, HOURS_STEP } from '@vacationtracker/shared/functions/work-week'
import { dayNames } from '@vacationtracker/shared/types/calendar'
import IntlMessages from '../../util/IntlMessages'
import capitalize from 'lodash/capitalize'

interface WorkWeekTablePickerProps {
  firstDayOfWeek: 0 | 1
  workingHours: WorkingHoursMatrix[]
  startDate: Date
  onChange: (hours: WorkingHoursMatrix[]) => void
}

interface TableRecord {
  key: number
  day: React.ReactElement
}

// A matrix of [day, hours, weekIndex]
export type WorkingHoursMatrix = [number, number, number]

export const WorkWeekTablePicker: React.FC<WorkWeekTablePickerProps> = ({
  firstDayOfWeek,
  workingHours,
  startDate,
  onChange,
}) => {
  // Calculate initial week count from the matrix
  const initialWeekCount = workingHours.length > 0 
    ? Math.max(...workingHours.map(([,,week]) => week)) + 1 
    : 1
    
  const [weekCount, setWeekCount] = useState(initialWeekCount)
  const [localWorkingHours, setLocalWorkingHours] = useState(workingHours)
  const { formatMessage } = useIntl()

  const handleHoursChange = (dayIndex: number, weekIndex: number, hours: number | null) => {
    
    const newWorkingHours = [...localWorkingHours]
    const existingIndex = newWorkingHours.findIndex((hours: WorkingHoursMatrix) => 
      hours[0] === dayIndex && hours[2] === weekIndex
    )
    
    if (hours === null) {
      if (existingIndex > -1) {
        newWorkingHours.splice(existingIndex, 1)
      }
    } else {
      if (existingIndex > -1) {
        newWorkingHours[existingIndex] = [dayIndex, hours, weekIndex]
      } else {
        newWorkingHours.push([dayIndex, hours, weekIndex])
      }
    }
    
    setLocalWorkingHours(newWorkingHours)
  }

  useEffect(() => {
    onChange(localWorkingHours)
  }, [localWorkingHours])

  const getHoursValue = (dayIndex: number, weekIndex: number) => {
    const value = localWorkingHours.find(([day, _, week]) => 
      day === dayIndex && week === weekIndex
    )?.[1]
    return value ?? null
  }

  const days = Array.from({ length: 7 }, (_, i) => {
    const day = firstDayOfWeek === 1 ? (i + 1) % 7 : i
    return {
      key: day,
      day: <IntlMessages id={dayNames[day]} />,
    }
  })

  const columns: ColumnType<TableRecord>[] = [
    {
      title: capitalize(formatMessage({ id: 'app.day' })),
      dataIndex: 'day',
      key: 'day',
    },
    ...Array.from({ length: weekCount }, (_, weekIndex) => {
      const weekStartDate = new Date(startDate)
      weekStartDate.setDate(startDate.getDate() + (weekIndex * 7))
      
      return {
        title: (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>
              {weekStartDate.toLocaleDateString('en-US', { 
                day: 'numeric',
                month: 'short',
              })}
            </span>
            {weekIndex > 0 && (
              <Button 
                type="text" 
                danger 
                size="small"
                onClick={() => {
                  setWeekCount(prev => prev - 1)
                  setLocalWorkingHours(
                    localWorkingHours
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      .filter(([_, __, week]) => week !== weekIndex)
                      .map(([day, hours, week]) => {
                        if (week > weekIndex) {
                          return [day, hours, week - 1]
                        }
                        return [day, hours, week]
                      })
                  )
                }}
              >
                ✕
              </Button>
            )}
          </div>
        ),
        dataIndex: `week${weekIndex}`,
        key: `week${weekIndex}`,
        render: (_: any, record: TableRecord) => (
          <InputNumber
            min={0} // We need to allow 0, so users can remove hours
            max={HOURS_MAX}
            value={getHoursValue(record.key, weekIndex)}
            onChange={(hours) => handleHoursChange(record.key, weekIndex, hours)}
            placeholder={formatMessage({ id: 'app.hours' })}
            step={HOURS_STEP}
            precision={1}
            parser={(value) => {
              const parsed = parseFloat(value || '0')
              if (isNaN(parsed)) return 0
              const rounded = Math.round(parsed * 2) / 2
              if (rounded === 0.5) return 1 // We do not allow 0.5, but we need to allow 0
              return rounded
            }}
            formatter={(value) => value ? `${value}` : ''}
          />
        ),
      }
    }),
  ]

  return (
    <div style={{ width: '100%' }}>
      <Table 
        columns={columns} 
        dataSource={days} 
        pagination={false}
        scroll={{ x: true }}
        footer={() => (
          weekCount < 4 && (
            <Button 
              type="dashed" 
              onClick={() => setWeekCount(prev => prev + 1)}
              icon={<PlusOutlined />}
            >
              <IntlMessages id="app.weekAdd" />
            </Button>
          )
        )}
      />
    </div>
  )
}